import ErrorHandler from "components/Admin/Organizations/OrganizationDetail/OrganizationDataSources/components/ErrorHandler";
import { ModulePreview } from "components/Admin/Organizations/types/Module.type";
import ConversationsStore, {
    SidePanelType,
} from "components/Conversations/Stores/ConversationsStore";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import { useStore } from "utils/useStore";
import ConversationLabelingStore, {
    loadConvoModules,
    loadDataLabelModules,
    loadLicensedModule,
} from "./FocusedConversation/components/convoSidePanel/store/ConversationLabelingStore";

interface LabelConversationDialogProps {
    title: string;
    subtitle: string;
    isOpen: boolean;
    availableModules: ModulePreview[];
    onClose: () => void;
}

const LabelConversationDialog = observer(
    ({
        title,
        subtitle,
        isOpen,
        availableModules,
        onClose,
    }: LabelConversationDialogProps) => {
        const conversationStore = useStore(ConversationsStore);
        const dataLabelStore = useStore(ConversationLabelingStore);

        useEffect(() => {
            if (isOpen) {
                dataLabelStore.openLabelingDialog();
            }
        }, [dataLabelStore, isOpen]);

        const options = useMemo(() => {
            const modulesByCategory: Record<
                string,
                Array<{ name: string; id: string }>
            > = {};
            availableModules.forEach((module) => {
                const category = module.moduleCategory.name;
                if (!modulesByCategory[category]) {
                    modulesByCategory[category] = [];
                }
                modulesByCategory[category].push({
                    name: module.name,
                    id: module.id,
                });
            });

            const categories = Object.keys(modulesByCategory);
            if (categories.length === 0) {
                return [];
            }
            if (categories.length === 1) {
                return modulesByCategory[categories[0]];
            }
            return categories.map((category) => ({
                label: category,
                options: modulesByCategory[category],
            }));
        }, [availableModules]);

        const initDataLabelingDrawer = async () => {
            if (!dataLabelStore.selectedLicensedModuleId) {
                return;
            }

            const result = await dataLabelStore.openModuleInLabelingDrawer(
                dataLabelStore.selectedLicensedModuleId,
            );
            if (result.lockError) {
                dataLabelStore.dataLabelingLockError = true;
                return;
            }
            if (!result.ok) {
                return;
            }

            onClose();
            conversationStore.setSidePanel(SidePanelType.DataLabeling);
        };

        return (
            <ConfirmationDialog
                title={title}
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={initDataLabelingDrawer}
                subTitle={subtitle}
                content={
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "12px",
                            width: "450px",
                            alignSelf: "flex-start",
                        }}
                    >
                        <AcxSelect
                            options={options}
                            id="label-conversation-select-module"
                            inputLabel="Available Modules"
                            fullWidth
                            isLoading={dataLabelStore.getTaskLoadingV2(
                                loadDataLabelModules,
                            )}
                            valueField="id"
                            labelField="name"
                            onChange={(e) => {
                                dataLabelStore.selectedLicensedModuleId = e.id;
                            }}
                        />
                        <ErrorHandler
                            hasError={dataLabelStore.dataLabelingLockError}
                            showCloseButton={false}
                            errorMessage={
                                "Another user is currently labeling this conversation using the module you selected. Please select another conversation."
                            }
                        />
                    </div>
                }
                isLoading={
                    dataLabelStore.getTaskLoadingV2(loadConvoModules) ||
                    dataLabelStore.getTaskLoadingV2(loadLicensedModule)
                }
                confirmButtonDisabled={!dataLabelStore.selectedLicensedModuleId}
            />
        );
    },
);

export default LabelConversationDialog;
